import Relyance, { type ConsentService } from './relyance';

export type { ConsentService };

type ConsentCategory = 'performance' | 'functional' | 'targeting';

/**
 * Get the consent key for a specific category
 * These keys are defined in the admin panel
 * under Advanced > Consents
 */
export function getConsentKey(category: string) {
  return `cookie_consent_${category}`;
}

/**
 * Get the user's consent for a specific category
 */
export function getConsentForCategory(category: ConsentCategory): boolean {
  return Relyance.getConsentForCategory(category);
}

/**
 * Get the user's preferences for all categories
 */
export function getUserPreferences(): Record<ConsentCategory, boolean> {
  return Relyance.getUserPreferences();
}

/**
 * Store the user's preferences for all categories
 */
export function storeCookiePreferencesHelper(
  consentService: ConsentService
): void {
  if (!hasBannerInteraction()) {
    return;
  }

  Relyance.storeCookiePreferences(consentService);
}

/**
 * Determines if the user has made a selection
 */
export function hasBannerInteraction(): boolean {
  return Relyance.hasBannerInteraction();
}

/**
 * Opens the cookie preference dialog
 */
export function openCookiePreferenceDialog(): void {
  Relyance.showPreferenceCenter();
}

/**
 * Registers a callback to be invoked when consent changes.
 * Waits for the consent provider to be fully ready before registering.
 *
 * The callback also fires once when the SDK becomes ready, so consumers
 * that read consent at startup before the Relyance script loaded can sync
 * to the actual SDK consent value. Without this, returning users with
 * stored consent would have been read as "not consented" until they next
 * changed their preferences (Relyance only fires RELYANCE_CONSENT_UPDATE
 * on user-driven changes, not on initial SDK ready).
 */
export function onConsentChange(callback: () => void): void {
  Relyance.onRelyanceReady(() => {
    // Listen to RELYANCE_CONSENT_UPDATE for consent value changes
    window.Relyance?.agent?.addEventListener(
      Relyance.RELYANCE_CONSENT_UPDATE_NAME,
      callback
    );

    // ALSO listen to button click events to catch interactions that don't trigger
    // RELYANCE_CONSENT_UPDATE (e.g., provider switches where there's no previous
    // consent state to compare against)
    window.Relyance?.agent?.addEventListener(
      'RELYANCE_BANNER_BUTTON_CLICKED',
      callback
    );

    window.Relyance?.agent?.addEventListener(
      'RELYANCE_PREFERENCE_CENTER_BUTTON_CLICKED',
      callback
    );

    // Sync once now that the SDK has loaded. See JSDoc above for rationale.
    callback();
  });
}

/**
 * Exported object for side-effecty operations that interact with browser APIs.
 * This object can be stubbed in tests, unlike named function exports.
 *
 * Pure functions (getConsentForCategory, getUserPreferences, etc.) remain as
 * named exports for better tree-shaking and clearer imports.
 */
export const cookieConsentActions = {
  openPreferenceDialog: openCookiePreferenceDialog,
  storePreferences: storeCookiePreferencesHelper,
  onConsentChange: onConsentChange,
};
